import React, { FC, Fragment, ReactNode, useState } from 'react';
import moment from 'moment';
import { Trans } from 'react-i18next';
import { Grid } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  AreaChart,
  Area,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';

import { StatsGrouping, TrendingEngagementStat } from 'api';
import { Charting, Queries } from 'components';

interface IProps {
  emptyState: () => ReactNode;
}

const useStyles = makeStyles({
  totals: {
    position: 'absolute',
  },
  block: {
    display: 'inline-flex',
    width: 6,
    height: 12,
    borderRadius: 4,
  },
});

const Activity: FC<IProps> = ({ emptyState }) => {
  const classes = useStyles();
  const theme = useTheme();

  const [variables, setVariables] = useState({
    grouping: StatsGrouping.Month,
    value: moment().month() + 1,
  });

  const postsColor = theme.palette.secondary.main;
  const sharesColor = theme.palette.primary.main;
  const clicksColor = theme.custom.palette.orange;

  function renderTotals(userDailyStats: TrendingEngagementStat) {
    const { total_posts, total_shares, total_clicks } = userDailyStats;

    return (
      <Grid container className={classes.totals}>
        <Grid item xs={4}>
          <Charting.AreaLegendItem
            title={<Trans i18nKey="common:post_plural" />}
            color={postsColor}
            value={total_posts}
          />
        </Grid>
        <Grid item xs={4}>
          <Charting.AreaLegendItem
            title={<Trans i18nKey="common:share_plural" />}
            color={sharesColor}
            value={total_shares}
          />
        </Grid>
        <Grid item xs={4}>
          <Charting.AreaLegendItem
            title={<Trans i18nKey="common:click_plural" />}
            color={clicksColor}
            value={total_clicks}
          />
        </Grid>
      </Grid>
    );
  }

  function renderChart(userDailyStats: TrendingEngagementStat) {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={userDailyStats.data} margin={{ top: 60, right: 0, left: 0, bottom: 0 }}>
          <CartesianGrid />
          <XAxis tickLine={false} dataKey="calendar_day" tickMargin={10} />
          <YAxis tickLine={false} orientation="right" tickMargin={10} />
          <Tooltip />
          <Area type="monotone" dataKey="posts" stackId="1" stroke={postsColor} fill={postsColor} />
          <Area
            type="monotone"
            dataKey="shares"
            stackId="1"
            stroke={sharesColor}
            fill={sharesColor}
          />
          <Area
            type="monotone"
            dataKey="clicks"
            stackId="1"
            stroke={clicksColor}
            fill={clicksColor}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  return (
    <Charting.Widget
      title={<Trans i18nKey="common:activity" />}
      variables={variables}
      onChange={setVariables}
    >
      <Queries.UserDailyStats loader="standard" {...variables}>
        {userDailyStats => {
          if (!userDailyStats.data.length) {
            return emptyState();
          }

          return (
            <Fragment>
              {renderTotals(userDailyStats)}
              {renderChart(userDailyStats)}
            </Fragment>
          );
        }}
      </Queries.UserDailyStats>
    </Charting.Widget>
  );
};

export default Activity;
